<template>
    <div class="cropper-box">
        <div class="bg-loader">
            <div class="lds-ripple">
                <div></div>
                <div></div>
            </div>
        </div>
        <cropper
            ref="cropper"
            class="cropper"
            :src="imgSrc.img_url"
            :stencil-size="stencilSize"
            :stencil-props="stencilProps"
            :default-size="defaultSize"
            :transitions="false"
            :debounce="false"
            :min-width="50"
            :min-height="50"
            image-restriction="stencil"
            @change="onChange"
        />
        <slider-bar
            ref="slideBar"
            :min="0"
            :max="100"
            :dotSize="30"
            :step="1"
            :disabled="!isImageLoaded"
            color="#000"
            class="text-white mt-5 mt-10 mb-5 mx-5"
            @change="
                val => {
                    onZoom(val)
                }
            "
        >
        </slider-bar>
        <div class="crop-footer">
            <div>
                <button
                    @click="cropImage"
                    :class="{ 'crop-btn': true, 'system-button-color system-button-text-color': !backend }"
                    :disabled="!isImageLoaded"
                    type="button"
                    id="crop-button"
                >
                    {{ translate('upload_modal_button_crop_image', 'Crop') }}
                </button>
            </div>
            <div class="pl-4">
                <button
                    @click="dontCropImage"
                    :class="{ 'crop-btn': true, 'system-button-color system-button-text-color': !backend }"
                    :disabled="!isImageLoaded"
                    type="button"
                    id="dont-crop-button"
                >
                    {{ translate('upload_modal_button_dont_crop_image', `Don't crop`) }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { Cropper } from 'vue-advanced-cropper'
import SliderBar from '@/components/SliderBar.vue'

export default {
    components: {
        Cropper,
        SliderBar
    },

    props: {
        imgSrc: {
            type: Object
        },

        keepRatioCropArea: {
            type: Boolean,
            default: false
        }
    },

    data() {
        return {
            zoom: 0,
            stencilProps: {
                movable: false,
                scalable: false,
                handlers: {},
                lines: {}
            },
            backend: false,
            isImageLoaded: false
        }
    },

    mounted() {
        this.stencilProps.aspectRatio = 1 / 1

        if (this.keepRatioCropArea) {
            this.initKeepAspectRatio()
        }

        if (typeof process.env.VUE_APP_IS_BACKEND !== 'undefined') {
            this.backend = true
        }
    },

    methods: {
        initKeepAspectRatio() {
            if (!this.imgSrc.image_original) {
                return 1 / 1
            }

            const img = new Image()
            var self = this
            img.src = this.imgSrc.image_original
            img.onload = function () {
                self.stencilProps.aspectRatio = this.width / this.height
            }
        },

        defaultSize({ imageSize }) {
            return {
                width: Math.min(imageSize.height, imageSize.width),
                height: Math.min(imageSize.height, imageSize.width)
            }
        },

        stencilSize({ boundaries }) {
            return {
                width: Math.min(boundaries.height, boundaries.width) - 48,
                height: Math.min(boundaries.height, boundaries.width) - 48
            }
        },

        onChange() {
            const cropper = this.$refs.cropper

            if (cropper) {
                const { coordinates, imageSize } = cropper
                if (imageSize.width / imageSize.height > coordinates.width / coordinates.height) {
                    // Determine the position of slider bullet
                    // It's 0 if the stencil has the maximum size and it's 1 if the has the minimum size
                    this.zoom =
                        (cropper.imageSize.height - cropper.coordinates.height) /
                        (cropper.imageSize.height - cropper.sizeRestrictions.minHeight)
                } else {
                    this.zoom =
                        (cropper.imageSize.width - cropper.coordinates.width) /
                        (cropper.imageSize.width - cropper.sizeRestrictions.minWidth)
                }

                this.$nextTick(() => {
                    this.zoom = Math.ceil(this.zoom * 100)
                    if (this.$refs.slideBar) {
                        this.$refs.slideBar.setValue(this.zoom)
                    }
                    document.querySelector('.cropper-box .bg-loader').style.opacity = 0
                    document.querySelector('.cropper-box .bg-loader').style.pointerEvents = 'none'

                    this.isImageLoaded = true
                })
            }
        },

        onZoom(value) {
            const cropper = this.$refs.cropper
            value = value / 100
            this.zoom = this.zoom / 100
            if (cropper) {
                if (cropper.imageSize.height < cropper.imageSize.width) {
                    const minHeight = cropper.sizeRestrictions.minHeight
                    const imageHeight = cropper.imageSize.height
                    // Determine the current absolute zoom and the new absolute zoom
                    // to calculate the sought relative zoom value
                    cropper.zoom(
                        (imageHeight - this.zoom * (imageHeight - minHeight)) /
                            (imageHeight - value * (imageHeight - minHeight))
                    )
                } else {
                    const minWidth = cropper.sizeRestrictions.minWidth
                    const imageWidth = cropper.imageSize.width
                    cropper.zoom(
                        (imageWidth - this.zoom * (imageWidth - minWidth)) /
                            (imageWidth - value * (imageWidth - minWidth))
                    )
                }
            }

            this.$refs.slideBar.setValue(this.zoom)
        },

        setStencilPosition(coordinates) {
            this.$refs.cropper.setCoordinates({
                width: coordinates.width,
                height: coordinates.height,
                left: coordinates.left,
                top: coordinates.top
            })
        },

        cropImage() {
            document.querySelector('#crop-button').disabled = true
            const { canvas } = this.$refs.cropper.getResult()
            const coordinates = this.$refs.cropper.coordinates
            const imageName = this.imgSrc.image_name || 'image.jpeg'

            canvas.toBlob(blob => {
                const myFile = new File([blob], imageName, {
                    type: blob.type
                })

                const event = {
                    target: {
                        files: [myFile]
                    }
                }

                event.target.files[0].source = this.imgSrc.source || ''
                event.target.files[0].image_original = this.imgSrc.image_original
                event.target.files[0].external =
                    this.imgSrc.external || (this.imgSrc.source && this.imgSrc.source != '') ? true : false
                event.target.files[0].coordinates = {
                    width: coordinates.width,
                    height: coordinates.height,
                    top: coordinates.top,
                    left: coordinates.left
                }

                this.$emit('cropImage', event)
            }, 'image/jpeg')
        },

        async dontCropImage() {
            document.querySelector('#dont-crop-button').disabled = true

            const data = this.imgSrc

            const image = data.img_url
            const imageOriginal = data.image_original
            const imageSource = data.source

            const response = await fetch(image)
            const blob = await response.blob()

            let imageType

            if (!blob.type) {
                imageType = `image/${image.split('?')[0].split('.').pop()}`
            }

            const img_name = blob.type === 'image/gif' ? 'image.gif' : 'image.jpeg'
            const file = new File([blob], img_name, { type: blob.type ? blob.type : imageType })

            file.source = imageSource
            file.image_original = imageOriginal

            // If not cropped already or old image (not have coordinate)
            if (!this.imgSrc.firstTimeSelect && (!data.coordinates || Object.keys(data.coordinates).length === 0)) {
                file.preventCrop = true
            }

            file.coordinates = {}

            const bgFile = {
                target: {
                    files: [file]
                }
            }

            this.$emit('cropImage', bgFile)
        },

        translate(key, msg) {
            if (typeof process.env.VUE_APP_IS_BACKEND !== 'undefined') {
                return msg
            }

            return this.$t(key)
        }
    },

    created() {
        this.zoom = 0
        // document.querySelector(".cropper-box .bg-loader").style.opacity = 1
    }
}
</script>

<style lang="scss">
.cropper-box {
    .loader {
        position: fixed;
        top: 0;
        left: 0;
    }

    .cropper {
        width: 300px;
        height: 300px;
        margin: auto;
    }

    .crop-btn {
        width: auto;
        height: 40px;
        margin: auto;
        padding: 0 24px;
        display: block;
        font-size: 16px;

        &[disabled] {
            opacity: 0.7;
        }
    }

    .bg-loader {
        width: 100%;
        height: 100%;
        position: absolute;
        top: 0;
        left: 0;
        background-color: var(--select-light-color);
        display: flex;
        justify-content: center;
        align-items: center;
        transition: opacity 0.5s;
        z-index: 999;

        .lds-ripple {
            display: inline-block;
            position: relative;
            width: 80px;
            height: 80px;
        }

        .lds-ripple div {
            position: absolute;
            border: 4px solid;
            opacity: 1;
            border-radius: 50%;
            animation: lds-ripple 1s cubic-bezier(0, 0.2, 0.8, 1) infinite;
            color: #ef0000;
        }

        .lds-ripple div:nth-child(2) {
            animation-delay: -0.5s;
            color: #ef0000;
        }

        @keyframes lds-ripple {
            0% {
                top: 36px;
                left: 36px;
                width: 0;
                height: 0;
                opacity: 1;
            }

            100% {
                top: 0px;
                left: 0px;
                width: 72px;
                height: 72px;
                opacity: 0;
            }
        }
    }

    .crop-footer {
        display: flex;
        justify-content: center;
    }

    @media screen and (max-width: 767px) {
        .mx-5 {
            margin-left: 8px;
            margin-right: 8px;
        }
    }
}
</style>
